// Aba Visão geral do painel Aplicativo: quantas pessoas têm acesso, quantas
// estão USANDO, o que as vitrines estão rendendo, e 14 dias de série.
import { useState } from 'react'
import { FiUsers, FiActivity, FiDownload, FiCamera, FiInbox, FiEye, FiMessageCircle, FiShoppingBag, FiShare2, FiKey, FiShieldOff } from 'react-icons/fi'
import { API_APP, useApp, Carregando, Falha, Cabeca, Chip, Numero, Segmento, PAPEL, num, quando, titulo } from './comum'

type Visao = {
  contas: {
    com_direito: number; total: number; ativas: number; usando_7d: number; instalaram: number; com_foto: number
    bloqueadas: number; troca_pendente: number; desativadas: number; novas_7d: number; fila_acesso: number
    por_papel: { papel: string; total: number; usando_7d: number }[]
  }
  entradas_7d: { vezes: number; pessoas: number }
  vitrine: Record<'d7' | 'd30', { visitas: number; folhearam: number; pelo_status?: number; pelo_story?: number; links_abertos: number; chamados: number; artes: number; pedidos: number; confirmados: number }>
  senhas_7d: Record<string, number>
  recusas_7d: number
  serie: { dia: string; visitas: number; pedidos: number; entraram: number; artes: number; contas_novas: number }[]
  cidades: { cidade: string; uf: string | null; n: number }[]
  produtos: { produto_id: number; nome?: string; sku?: string; vistas: number; quiseram: number; chamados: number }[]
  historico_entradas_desde: string | null
}

const pct = (a: number, b: number) => (b ? `${Math.round((a / b) * 100)}%` : '—')

export function VisaoGeral({ irPara }: { irPara: (aba: string, filtro?: string) => void }) {
  const { dados, erro, carregando, recarregar } = useApp<Visao>(`${API_APP}/visao`)
  const [janela, setJanela] = useState<'d7' | 'd30'>('d7')
  const [serieDe, setSerieDe] = useState<'visitas' | 'entraram' | 'artes' | 'contas_novas'>('visitas')

  if (!dados) return erro ? <Falha erro={erro} tentar={recarregar} /> : <Carregando />
  const c = dados.contas
  const v = dados.vitrine[janela]
  const maxSerie = Math.max(1, ...dados.serie.map(s => s[serieDe]))
  const semAcesso = Math.max(c.com_direito - c.ativas, 0)

  return (
    <div className="h-full min-h-0 flex flex-col gap-3 p-4 sm:p-5 overflow-y-auto lg:overflow-hidden">
      <Cabeca
        titulo="Visão geral do app"
        sub="app.twplanalto.com.br — o app Luísa, das consultoras, líderes e empresários"
        carregando={carregando} recarregar={recarregar}
        chips={<>
          <Chip title="Cadastros do Draco com direito ao app (fora dos distritos de depósito)">{num(c.com_direito)} com direito</Chip>
          <Chip tom="bom">{num(c.ativas)} com acesso · {pct(c.ativas, c.com_direito)}</Chip>
          <Chip>{num(semAcesso)} ainda sem acesso</Chip>
          {c.fila_acesso > 0 && <Chip tom="alerta" onClick={() => irPara('acessos')}>{num(c.fila_acesso)} na fila de acesso</Chip>}
          {c.bloqueadas > 0 && <Chip tom="ruim" onClick={() => irPara('pessoas', 'bloqueadas')}>{num(c.bloqueadas)} bloqueadas</Chip>}
          {c.troca_pendente > 0 && <Chip tom="info" onClick={() => irPara('pessoas', 'troca_pendente')}>{num(c.troca_pendente)} com troca de senha pendente</Chip>}
          {c.desativadas > 0 && <Chip onClick={() => irPara('pessoas', 'desativadas')}>{num(c.desativadas)} desativadas</Chip>}
        </>}
      />

      {/* ── as pessoas ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5 shrink-0">
        <button type="button" onClick={() => irPara('pessoas', 'usando')} className="text-left">
          <Numero icon={FiActivity} rotulo="Usando (7 dias)" valor={num(c.usando_7d)} nota={`${pct(c.usando_7d, c.ativas)} de quem tem acesso`} tom="bom" />
        </button>
        <button type="button" onClick={() => irPara('pessoas', 'paradas')} className="text-left">
          <Numero icon={FiUsers} rotulo="Paradas" valor={num(Math.max(c.ativas - c.usando_7d, 0))} nota="sem abrir o app há 7 dias" tom={c.ativas - c.usando_7d > 0 ? 'alerta' : undefined} />
        </button>
        <button type="button" onClick={() => irPara('pessoas', 'nunca_instalou')} className="text-left">
          <Numero icon={FiDownload} rotulo="Instalaram" valor={num(c.instalaram)} nota={`${pct(c.instalaram, c.ativas)} de quem tem acesso`} />
        </button>
        <button type="button" onClick={() => irPara('pessoas', 'sem_foto')} className="text-left">
          <Numero icon={FiCamera} rotulo="Com foto" valor={num(c.com_foto)} nota={`${num(Math.max(c.ativas - c.com_foto, 0))} sem foto`} />
        </button>
        <button type="button" onClick={() => irPara('seguranca')} className="text-left col-span-2 md:col-span-1">
          <Numero icon={FiKey} rotulo="Senhas (7 dias)" valor={num(Object.values(dados.senhas_7d).reduce((a, b) => a + b, 0))}
            nota={`${num(dados.senhas_7d.recuperacao || 0)} esqueceram · ${num(dados.recusas_7d)} recusas`} />
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 lg:flex-1 lg:min-h-0">
        {/* ── as vitrines + série ───────────────────────────────────────── */}
        <section className="neu-card rounded-2xl p-4 flex flex-col gap-3 lg:min-h-0 lg:col-span-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h3 className="text-[13px] font-semibold">Vitrines — o link de cada uma</h3>
            <Segmento rotulo="Período" valor={janela} aoMudar={setJanela} opcoes={[['d7', '7 dias'], ['d30', '30 dias']]} />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 2xl:grid-cols-6 gap-2">
            {([
              [FiEye, 'Visitas', v.visitas, `${num(v.folhearam)} folhearam`],
              [FiUsers, 'Vitrines abertas', v.links_abertos, 'links com visita'],
              [FiMessageCircle, 'Chamados', v.chamados, 'clientes no WhatsApp'],
              [FiShoppingBag, 'Pedidos', v.pedidos, `${num(v.confirmados)} confirmados`],
              [FiShare2, 'Artes', v.artes, `compartilhadas · visitas: ${num(v.pelo_status || 0)} status, ${num(v.pelo_story || 0)} story`],
              [FiInbox, 'Entradas', dados.entradas_7d.vezes, `${num(dados.entradas_7d.pessoas)} pessoas · 7 dias`],
            ] as const).map(([Icon, r, val, nota]) => (
              <div key={r} className="neu-inset rounded-xl px-2.5 py-2 min-w-0">
                <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground truncate"><Icon className="w-3 h-3 shrink-0" />{r}</span>
                <span className="block text-[18px] font-semibold tabular-nums leading-tight">{num(val)}</span>
                <span className="block text-[10.5px] text-muted-foreground truncate" title={nota}>{nota}</span>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between gap-2 flex-wrap mt-1">
            <h4 className="text-[12px] font-semibold text-muted-foreground">Últimos 14 dias</h4>
            <Segmento rotulo="Série" valor={serieDe} aoMudar={setSerieDe}
              opcoes={[['visitas', 'Visitas'], ['entraram', 'Entradas'], ['artes', 'Artes'], ['contas_novas', 'Contas']]} />
          </div>
          <div className="h-40 lg:h-auto lg:flex-1 lg:min-h-[140px] flex items-end gap-1.5" role="img" aria-label={`Série de ${serieDe} dos últimos 14 dias`}>
            {dados.serie.map(s => {
              const val = s[serieDe]
              return (
                <div key={s.dia} className="flex-1 h-full flex flex-col justify-end items-center gap-1 min-w-0" title={`${s.dia.slice(8, 10)}/${s.dia.slice(5, 7)}: ${num(val)}`}>
                  <span className="text-[10px] tabular-nums text-muted-foreground">{val ? num(val) : ''}</span>
                  <div className="w-full rounded-t-md bg-primary/70" style={{ height: `${Math.max(val ? 4 : 1, (val / maxSerie) * 100)}%`, opacity: val ? 1 : 0.25 }} />
                  <span className="text-[9.5px] tabular-nums text-muted-foreground">{s.dia.slice(8, 10)}</span>
                </div>
              )
            })}
          </div>
          {serieDe === 'entraram' && dados.historico_entradas_desde && (
            <p className="text-[10.5px] text-muted-foreground">O histórico de entradas começou em {quando(dados.historico_entradas_desde)} — antes disso só existia o último login.</p>
          )}
        </section>

        {/* ── por papel, cidades e produtos ─────────────────────────────── */}
        <section className="neu-card rounded-2xl p-4 flex flex-col gap-3 lg:min-h-0 lg:overflow-y-auto scrollbar-hidden">
          <div>
            <h3 className="text-[13px] font-semibold mb-2">Por papel</h3>
            <div className="flex flex-col gap-2">
              {[...c.por_papel].sort((a, b) => b.total - a.total).map(p => (
                <div key={p.papel} className="text-[12px]">
                  <div className="flex justify-between gap-2"><span>{PAPEL[p.papel] || p.papel}</span><span className="tabular-nums text-muted-foreground">{num(p.usando_7d)} de {num(p.total)} usando</span></div>
                  <div className="h-1.5 rounded-full bg-foreground/[0.07] overflow-hidden mt-1">
                    <div className="h-full rounded-full bg-emerald-500/80" style={{ width: pct(p.usando_7d, p.total) }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h3 className="text-[13px] font-semibold mb-1.5">Cidades que abriram (30 dias)</h3>
            {dados.cidades.length ? dados.cidades.map(x => (
              <div key={`${x.cidade}-${x.uf}`} className="flex justify-between gap-2 text-[12px] py-0.5">
                <span className="truncate">{titulo(x.cidade)}{x.uf ? ` · ${x.uf}` : ''}</span><span className="tabular-nums text-muted-foreground">{num(x.n)}</span>
              </div>
            )) : <p className="text-[12px] text-muted-foreground">Nenhuma visita ainda.</p>}
          </div>
          <div>
            <h3 className="text-[13px] font-semibold mb-1.5">Produtos mais vistos (30 dias)</h3>
            {dados.produtos.length ? dados.produtos.map(p => (
              <div key={p.produto_id} className="flex justify-between gap-2 text-[12px] py-0.5" title={p.sku ? `cód. ${p.sku}` : undefined}>
                <span className="truncate">{p.nome || `produto ${p.produto_id}`}</span>
                <span className="tabular-nums text-muted-foreground shrink-0">{num(p.vistas)} vistas{p.chamados ? ` · ${num(p.chamados)} chamados` : ''}</span>
              </div>
            )) : <p className="text-[12px] text-muted-foreground">Nenhum produto visto ainda.</p>}
          </div>
          <p className="text-[10.5px] text-muted-foreground mt-auto"><FiShieldOff className="inline w-3 h-3 mr-1" />Senha, CPF e telefone inteiro nunca saem do app.</p>
        </section>
      </div>
    </div>
  )
}
